export const statusColors = {
  Lead: '#5DADE2',
  'Lead - DNC': '#8B0000',
  Sourcing: '#FFCC00',
  Onboarded: '#006400',
  Active: '#58D68D',
  Paused: '#F39C12',
  Inactive: '#95A5A6',
  Terminated: '#A93226',
  Duplicate: '#ff8c00',
};

// Status color families for visual progression
export const statusColorFamilies = {
  prospects: {
    name: 'Prospects',
    baseHue: 160, // Green-blue base
    statuses: ['New', 'Attempt to Contact', 'Engaged', 'Discovery'],
  },
  proposed: {
    name: 'Proposed',
    baseHue: 35, // Orange base
    statuses: ['Proposal', 'Refinement'],
  },
  agreement: {
    name: 'Agreement',
    baseHue: 210, // Blue base
    statuses: ['Contract Sent'],
  },
  closed: {
    name: 'Closed',
    baseHue: 120, // Green base for Won, distinct colors for others
    statuses: ['Closed Won', 'Closed Lost', 'Closed Dead'],
  },
};

// Define the new status mappings with gradient family colors
export const newStatuses = {
  // Prospects family - green gradient getting darker as progress advances
  New: '#B8E6D3', // Lightest (early stage)
  'Attempt to Contact': '#88D9B4', // Light green
  Engaged: '#5BC98A', // Medium green (making contact)
  Discovery: '#2EB967', // Darkest green (closest to proposal)

  // Proposed family - orange gradient getting more intense
  Proposal: '#F5C37A', // Lighter orange
  Refinement: '#E2A855', // Deeper orange (closer to agreement)

  // Agreement family - distinct blue to show contract stage
  'Contract Sent': '#4A90E2',

  // Closed family - distinct colors for final outcomes
  'Closed Won': '#28A745', // Success green
  'Closed Lost': '#DC3545', // Warning red
  'Closed Dead': '#6C757D', // Neutral gray
};

// Helper functions for status color families
export const getStatusFamily = status => {
  for (const [familyKey, family] of Object.entries(statusColorFamilies)) {
    if (family.statuses.includes(status)) {
      return { key: familyKey, ...family };
    }
  }
  return null;
};

export const getStatusProgressInFamily = status => {
  const family = getStatusFamily(status);
  if (!family) return null;

  const index = family.statuses.indexOf(status);
  return {
    family: family.name,
    position: index + 1,
    total: family.statuses.length,
    percentage: ((index + 1) / family.statuses.length) * 100,
  };
};

export const isStatusInFamily = (status, familyKey) => {
  const family = statusColorFamilies[familyKey];
  return family ? family.statuses.includes(status) : false;
};

export const types = {
  HOA: '',
  COA: '',
  Cooperative: '',
  'Master-Planned': '',
  Unknown: '',
};

export const probabilities = {
  New: 0.1,
  'Attemt to Contact': 0.2,
  Engaged: 0.4,
  Discovery: 0.5,
  Proposal: 0.6,
  Refinement: 0.7,
  'Contract Sent': 0.8,
  Win: 1,
};

export const bodPositions = ['President', 'Non Board', 'Board Member', 'Owner', 'Other'];

export const amenities = [
  'Basketball Court',
  'Boat Moorage',
  'Cabana',
  'Club House',
  'Elevator',
  'Fitness Center',
  'Hot Tub',
  'Laundry',
  'Monument Sign',
  'Park and Green Areas',
  'Parks and Trails',
  'Paved Walking Trails',
  'Playground',
  'Pool',
  'Sports Court',
  'Tennis Court',
];

export const communicationTypes = {
  'Internal Note': '#c9b06e',
  'Phone Call': '#35495e',
  Email: '#42b883',
  Text: '#347474',
  'In Person': '#ff7e67',
  Meeting: '#ff7e60',
  Other: 'rgba(0,0,0,.6)',
};

export const leadTypes = ['New Customer', 'Renewal', 'Retention', 'Change Order', 'Customer'];

export const leadPriorities = ['Low', 'Medium', 'High'];

// Funnel stage groups for advanced filtering
export const funnelStages = {
  'Early Stage': ['New', 'Attempt to Contact'],
  'Discovery': ['Engaged', 'Discovery'],
  'Proposal': ['Proposal', 'Refinement'],
  'Contract': ['Contract Sent'],
  'Closed': ['Closed Won', 'Closed Lost', 'Closed Dead'],
};

// Enhanced Stage-Based Status System with Heat Progression Colors
export const statusStages = {
  'Lead Stage': {
    name: 'Lead Stage',
    color: '#1e3a8a', // Cool blue start
    hoverColor: '#3b82f6', // Lighter blue on hover
    statuses: ['New', 'Attempt to Contact', 'Engaged'],
    order: 1,
  },
  'Discovery Stage': {
    name: 'Discovery Stage', 
    color: '#0ea5e9', // Warmer blue
    hoverColor: '#38bdf8',
    statuses: ['Discovery'],
    order: 2,
  },
  'Proposal Stage': {
    name: 'Proposal Stage',
    color: '#f97316', // Orange start
    hoverColor: '#ea580c', // Darker orange on hover
    statuses: ['Proposal', 'Refinement'],
    order: 3,
  },
  'Contract Stage': {
    name: 'Contract Stage',
    color: '#dc2626', // Red-orange
    hoverColor: '#b91c1c',
    statuses: ['Contract Sent'],
    order: 4,
  },
  'Closed Stage': {
    name: 'Closed Stage',
    // Outcome-specific colors (no single stage color)
    color: '#6B7280', // Neutral gray as default
    hoverColor: '#4B5563',
    statuses: ['Closed Won', 'Closed Lost', 'Closed Dead'],
    order: 5,
  },
};

// Helper function to get the stage for a given status
export const getStatusStage = (status) => {
  for (const [stageKey, stage] of Object.entries(statusStages)) {
    if (stage.statuses.includes(status)) {
      return { key: stageKey, ...stage };
    }
  }
  return null;
};

// Helper function to get all statuses for selected stages
export const getStatusesForStages = (selectedStages) => {
  if (!selectedStages || selectedStages.size === 0) return [];
  
  const statuses = [];
  selectedStages.forEach(stageKey => {
    const stage = statusStages[stageKey];
    if (stage) {
      statuses.push(...stage.statuses);
    }
  });
  return [...new Set(statuses)]; // Remove duplicates
};

// Helper function to check if a status matches selected stages
export const statusMatchesStages = (status, selectedStages) => {
  if (!selectedStages || selectedStages.size === 0) return true;
  return getStatusesForStages(selectedStages).includes(status);
};

// Get ordered stages for UI display
export const getOrderedStages = () => {
  return Object.entries(statusStages)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([key, stage]) => ({ key, ...stage }));
};

// Updated status colors to include stage awareness for Closed outcomes
export const enhancedStatusColors = {
  ...newStatuses,
  // Override closed stage colors for visual distinction
  'Closed Won': '#28A745', // Success green
  'Closed Lost': '#6C757D', // Neutral gray  
  'Closed Dead': '#95A5A6', // Light gray
};

// Strategic account filter options
export const strategicAccountOptions = ['Yes', 'No'];

// Standard size bucket ranges used for filtering and deal setup
export const sizeBuckets = [
  'N/A',
  '1-25',
  '26-50',
  '51-75',
  '76-100',
  '101-125',
  '126-150',
  '151-200',
  '201-225',
  '226-250',
  '251-275',
  '276-300',
  '301-more',
];

// Helper function to calculate size bucket based on number of doors/units
export const calculateSizeBucket = doors => {
  const numDoors = Number(doors) || 0;
  if (numDoors === 0) return 'N/A';
  if (numDoors <= 25) return '1-25';
  if (numDoors <= 50) return '26-50';
  if (numDoors <= 75) return '51-75';
  if (numDoors <= 100) return '76-100';
  if (numDoors <= 125) return '101-125';
  if (numDoors <= 150) return '126-150';
  if (numDoors <= 200) return '151-200';
  if (numDoors <= 225) return '201-225';
  if (numDoors <= 250) return '226-250';
  if (numDoors <= 275) return '251-275';
  if (numDoors <= 300) return '276-300';
  return '301-more';
};

// Helper function to calculate lead priority based on number of doors/units
// <25 doors = Low, >90 doors = High, else = Medium
export const calculateLeadPriority = doors => {
  const numDoors = Number(doors) || 0;
  if (numDoors < 25) return 'Low';
  if (numDoors > 90) return 'High';
  return 'Medium';
};

// Helper function to calculate comprehensive lead score (replaces lead health)
export const calculateLeadScore = association => {
  let score = 50; // Start with neutral score
  const recommendations = [];
  const factors = [];
  
  // Strategic Account Flag - 2x multiplier applied at end
  const isStrategicAccount = !!(association.strategicAccountReason && association.strategicAccountReason.trim());
  if (isStrategicAccount) {
    factors.push('⭐ Strategic Account Priority');
  }

  // Recent Engagement Boost - Strong prioritization for active prospects
  const engagementScore = association.engagementScore || 0;
  const lastEngagementDate = association.lastEngagementDate;
  if (lastEngagementDate) {
    const hoursAgo = (Date.now() - new Date(lastEngagementDate)) / (1000 * 60 * 60);
    if (hoursAgo < 24) {
      score += 30;
      factors.push('🔥 Recent engagement (<24 hours)');
      recommendations.unshift('HOT LEAD: Follow up immediately while engaged!');
    } else if (hoursAgo < 168) { // 7 days
      score += 20;
      factors.push('⚡ Active engagement (<7 days)');
      recommendations.push('Maintain engagement momentum');
    } else if (hoursAgo < 720) { // 30 days
      score += 10;
      factors.push('📅 Recent engagement (<30 days)');
    }
  }
  
  // Engagement tracking integration
  if (engagementScore > 0) {
    score += Math.min(engagementScore / 2, 25); // Cap engagement bonus at 25 pts
    if (engagementScore >= 60) {
      factors.push('🔥 Hot Lead - High engagement score');
    } else if (engagementScore >= 30) {
      factors.push('⚡ Warm Lead - Active engagement');
    }
  }

  // Age factor (how long since initial lead date)
  const initialLeadDate = association.initialLeadDate;
  if (initialLeadDate) {
    const daysOld = Math.floor((Date.now() - new Date(initialLeadDate)) / (1000 * 60 * 60 * 24));
    if (daysOld < 30) {
      score += 15;
      factors.push('✅ Fresh lead (<30 days)');
    } else if (daysOld < 90) {
      score += 5;
      factors.push('⚠️ Aging lead (30-90 days)');
    } else {
      score -= 10;
      factors.push('🔴 Old lead (>90 days)');
      recommendations.push('Consider refreshing contact or moving to nurture campaign');
    }
  }

  // Status progression with Discovery gate for business intelligence
  const statusProgression = {
    New: 0,
    'Attempt to Contact': 10,
    Engaged: 25,
    Discovery: 35,
    'Meeting Set': 40,
    Proposal: 50,
    Refinement: 60,
    'Contract Sent': 75,
    'Closed Won': 100,
    'Closed Lost': -50,
    'Closed Dead': -50,
    'Dead Lead/DNC': -50,
  };

  const statusScore = statusProgression[association.status] || 0;
  score += statusScore;
  const isDataVerified = statusScore >= 35; // Discovery+ status means data is verified
  
  if (statusScore > 40) {
    factors.push('✅ Advanced pipeline stage');
  } else if (statusScore > 20) {
    factors.push('⚠️ Mid-pipeline stage');
    recommendations.push('Push for next meeting or proposal opportunity');
  } else if (statusScore >= 0) {
    factors.push('🔴 Early pipeline stage');
    recommendations.push('Focus on building engagement and trust');
  }

  // Business Intelligence Scoring (only applies for Discovery+ status)
  if (isDataVerified) {
    // Unit count scaling with 100+ minimum threshold
    const unitCount = association.numberOfUnits || association.doors || 0;
    if (unitCount >= 100) {
      const unitMultiplier = Math.log10(unitCount / 100) + 1; // Logarithmic scaling
      const unitBonus = Math.min(unitMultiplier * 15, 40); // Cap at 40 pts
      score += unitBonus;
      factors.push(`🏢 Large property (${unitCount} units) +${Math.round(unitBonus)}pts`);
    }
    
    // Property type premium for shared-wall properties
    const propertyType = association.propertyType;
    if (propertyType === 'Condo') {
      score += 15;
      factors.push('🏢 Condo premium (+15pts)');
    } else if (propertyType === 'Townhouse') {
      score += 10;
      factors.push('🏘️ Townhouse premium (+10pts)');
    }
    
    // Association type hierarchy
    const associationType = association.type;
    if (associationType === 'COA') {
      score += 15;
      factors.push('🏢 COA premium (+15pts)');
    } else if (associationType === 'HOA') {
      score += 10;
      factors.push('🏠 HOA advantage (+10pts)');
    } else if (associationType === 'Cooperative') {
      score += 12;
      factors.push('🤝 Cooperative advantage (+12pts)');
    }
    
    // Self-managed preference (No = more attractive)
    if (association.selfManaged === 'No') {
      score += 8;
      factors.push('✅ Currently managed (+8pts)');
    }
  } else {
    factors.push('⚠️ Data unverified (pre-Discovery) - basic scoring only');
  }

  // Engagement strategy weighting
  const engagementStrategy = association.engagementStrategy;
  if (engagementStrategy === 'Pursue') {
    score += 20;
    factors.push('🎯 Pursue strategy (+20pts)');
  } else if (engagementStrategy === 'Nurture') {
    score += 10;
    factors.push('🌱 Nurture strategy (+10pts)');
  } else if (engagementStrategy === 'Stall') {
    score -= 20;
    factors.push('🔴 Stalled strategy (-20pts)');
    recommendations.push('Re-evaluate strategy or move to nurture');
  }

  // Data completeness scoring
  let completenessScore = 0;
  if (association.numberOfUnits || association.doors) completenessScore += 5;
  if (association.monthlyRevenue) completenessScore += 5;
  if (association.email && association.phone) completenessScore += 5;
  if (association.annualIncome) completenessScore += 3;
  
  score += completenessScore;
  if (completenessScore >= 15) {
    factors.push('✅ Complete data profile');
  } else {
    factors.push('⚠️ Missing key data fields');
    recommendations.push('Gather missing property and financial details');
  }

  // Apply strategic account multiplier
  if (isStrategicAccount) {
    score *= 2;
    recommendations.unshift(`⭐ STRATEGIC ACCOUNT: ${association.strategicAccountReason}`);
  }

  // Normalize score to 0-100 range (or higher for strategic accounts)
  score = Math.max(0, Math.min(isStrategicAccount ? 200 : 100, score));

  // Determine score level (adjusted for strategic accounts)
  let scoreLevel, scoreColor, scoreIcon;
  if (score >= 140) {
    scoreLevel = 'Strategic Priority';
    scoreColor = '#FFD700';
    scoreIcon = '⭐';
  } else if (score >= 70) {
    scoreLevel = 'Excellent';
    scoreColor = '#2e7d32';
    scoreIcon = '💚';
  } else if (score >= 50) {
    scoreLevel = 'Good';
    scoreColor = '#388e3c';
    scoreIcon = '💛';
  } else if (score >= 30) {
    scoreLevel = 'Fair';
    scoreColor = '#f57c00';
    scoreIcon = '🧡';
  } else {
    scoreLevel = 'Poor';
    scoreColor = '#c62828';
    scoreIcon = '❤️';
  }

  // Add general recommendations based on score level
  if (score < 30) {
    recommendations.push('URGENT: Requires immediate attention or move to nurture/dead status');
  } else if (score < 50) {
    recommendations.push('Needs more active engagement to improve score');
  } else if (score >= 70) {
    recommendations.push('Excellent score - maintain momentum and push toward close');
  }

  return {
    score: Math.round(score),
    scoreLevel,
    scoreColor,
    scoreIcon,
    factors,
    recommendations: recommendations.slice(0, 6), // Allow more recommendations
    isStrategicAccount,
    // Legacy compatibility
    healthLevel: scoreLevel,
    healthColor: scoreColor,
    healthIcon: scoreIcon,
  };
};

// Backward compatibility alias
export const calculateLeadHealth = calculateLeadScore;

// Admin user configuration
export const adminUsers = {
  'mario.vaccari@condoshield.org': {
    name: 'Mario Vaccari',
    isAdmin: true,
  },
  'sarah@condoshield.com': {
    name: 'Sarah M',
    isAdmin: true,
  },
  'sarahm16@example.com': {
    name: 'Sarah M',
    isAdmin: true,
  },
};

// Helper function to check if user is admin
export const isUserAdmin = email => {
  if (!email) {
    if (import.meta.env.DEV) {
      console.log('🔐 isUserAdmin: No email provided');
    }
    return false;
  }

  const normalizedEmail = email.toLowerCase();
  const user = adminUsers[normalizedEmail];
  const result = user && user.isAdmin;

  // Debug logging for development
  if (import.meta.env.DEV && result) {
    console.log('🔐 Admin access granted for:', normalizedEmail);
  }

  return result;
};

// Test function for console capture moved to FetchDevConsole package
// Import with: import { testConsoleCapture } from 'fetch-dev-console';
